/**
 * Event Registry — the single authored source of the wire contract. The same event
 * names ride Socket.io between web and server and the raw `ws` { event, data }
 * envelope between server and runner (apps/sandbox/src/server/sandbox-socket.ts).
 *
 * ONE entry per event (keyed by its EVENTS wire name), carrying that event's
 * contract metadata: whether the relay forwards it sandbox→frontend, and (when
 * the payload has been given a Zod schema) the schema that authors its shape.
 *
 * Everything downstream is DERIVED from this table, so the four structures that
 * used to be hand-kept in parallel — the EventSchemas map, the FORWARD_EVENTS
 * allowlist, and their membership guards — can no longer silently drift:
 *   - {@link EventSchemas}       — the { name → Zod schema } validation map.
 *   - {@link FORWARD_EVENTS_SET} — O(1) membership set for the relay forward gate.
 *   - {@link FORWARD_EVENTS_DERIVED} — the forwarded names in EVENTS order.
 *   - {@link SCHEMALESS_EVENTS}  — the shrink-only allowlist of not-yet-schema'd events.
 *
 * The EVENTS constant (packages/shared/src/constants/events.ts) is the hand-authored name identity
 * and is intentionally NOT derived here: it must remain a zod-free leaf so that
 * importing a wire NAME never drags the schema graph into a browser bundle. This
 * table KEYS off EVENTS and is compile-time-exhaustive over it
 * (`satisfies Record<EventName, EventContract>`), so every event has exactly one
 * contract entry — a missing or stray name fails `tsc`.
 *
 * Byte-compatibility with the previously hand-written EventSchemas / FORWARD_EVENTS
 * is proven by tests/events/registry-bytecompat.test.ts.
 */

import type { z } from 'zod';
import { EVENTS, type EventName } from '../constants/events.js';
import { LineMutePayloadSchema, LiveEventPayloadSchema, LiveStatePayloadSchema } from '../composer/live.js';
import {
  AgentEndPayloadSchema,
  FilesChangedPayloadSchema,
  AgentReconstructionMismatchPayloadSchema,
  AgentHistoryHealedPayloadSchema,
  AgentHookStopContinuedPayloadSchema,
  AgentErrorPayloadSchema,
  AgentInterruptPayloadSchema,
  AgentNotebookPayloadSchema,
  AgentRegisterPayloadSchema,
  AgentResultPayloadSchema,
  AgentStartPayloadSchema,
  AgentTextDeltaPayloadSchema,
  AgentTextPayloadSchema,
  AgentThinkingDeltaPayloadSchema,
  AgentThinkingStartPayloadSchema,
  AgentThinkingPayloadSchema,
  AgentTodoPayloadSchema,
  AgentToolResultPayloadSchema,
  AgentToolUsePayloadSchema,
  BrowserControlDeferredPayloadSchema,
  BrowserMirrorEventPayloadSchema,
  BrowserMirrorResnapshotPayloadSchema,
  BrowserNavigatePayloadSchema,
  BrowserPaneVisibilityPayloadSchema,
  BrowserTabsPayloadSchema,
  BrowserStreamResumedPayloadSchema,
  BrowserStreamSuspendedPayloadSchema,
  BrowserViewingPayloadSchema,
  BrowserViewSyncPayloadSchema,
  ComputerWakePayloadSchema,
  ComputerSleepPayloadSchema,
  ComputerShowPayloadSchema,
  ComputerClosePayloadSchema,
  ComposerScreenPayloadSchema,
  DepositAckPayloadSchema,
  ConnectionHeartbeatPayloadSchema,
  MessageCreditUpdatePayloadSchema,
  NamiNotifyPayloadSchema,
  NamiSweepCompletePayloadSchema,
  OsPerceiveRequestPayloadSchema,
  OsPerceiveResultPayloadSchema,
  OsVerbRequestPayloadSchema,
  OsVerbResultPayloadSchema,
  SessionCompactedPayloadSchema,
  SessionConfigPayloadSchema,
  SessionCreatePayloadSchema,
  SessionCreatedPayloadSchema,
  SessionDestroyPayloadSchema,
  SessionListPayloadSchema,
  SessionResumePayloadSchema,
  SessionUpdatePayloadSchema,
  EngineAuthStatusPayloadSchema,
  EngineAuthLogoutPayloadSchema,
  EngineStandingPayloadSchema,
  EngineStandingRefreshPayloadSchema,
  EngineConnectPayloadSchema,
  EngineConnectCancelPayloadSchema,
  EngineConnectProgressPayloadSchema,
  EngineSlotsSyncPayloadSchema,
  PermissionDeniedPayloadSchema,
  TaskNoticePayloadSchema,
  SubagentMessagePayloadSchema,
  SubagentSpawnedPayloadSchema,
  SystemInitPayloadSchema,
  UserMessagePayloadSchema,
} from '../schemas/event-payloads.schema.js';
import {
  TaskCreatedPayloadSchema,
  TaskDestroyPayloadSchema,
  TaskListPayloadSchema,
  TaskOutputRegisteredPayloadSchema,
  TaskArtifactRegisteredPayloadSchema,
  TaskPreviewReadyPayloadSchema,
  TaskPreviewClosedPayloadSchema,
  AppsChangedPayloadSchema,
  AppsViewPayloadSchema,
  TaskResumeFailedPayloadSchema,
  TaskResumePayloadSchema,
  TaskResumedPayloadSchema,
  TaskStatsPayloadSchema,
  TaskStreamPayloadSchema,
  TaskSyncHintPayloadSchema,
  InjectionScreenNoticePayloadSchema,
  TaskTitleEditPayloadSchema,
  TaskUpdatePayloadSchema,
} from '../schemas/task-payloads.schema.js';
import { RunnerStatusPayloadSchema } from '../schemas/runner-status.schema.js';
import {
  BrowserCookiesRequestPayloadSchema, BrowserCookiesResultPayloadSchema, BrowserFetchRequestPayloadSchema, BrowserFetchResultPayloadSchema,
} from '../schemas/browser-fetch.schema.js';
import {
  BoxExecRequestPayloadSchema, BoxExecResultPayloadSchema, BoxFsRequestPayloadSchema, BoxFsResultPayloadSchema,
  BoxLogsRequestPayloadSchema, BoxLogsResultPayloadSchema,
} from '../schemas/box-door.schema.js';
import { BrowserWatchEventPayloadSchema, BrowserWatchStartPayloadSchema, BrowserWatchStopPayloadSchema } from '../schemas/browser-watch.schema.js';
import { AgentSpansPayloadSchema } from '../schemas/trace-spans.schema.js';
import { AgentStreamStalledPayloadSchema } from '../schemas/stream-stalled.schema.js';
import {
  TaskForkRequestPayloadSchema, TaskForkResultPayloadSchema, TaskRewindRequestPayloadSchema, TaskRewindResultPayloadSchema,
} from '../schemas/task-history.schema.js';
import {
  AgentCrudPayloadSchema,
  AgentInstalledPayloadSchema,
  AgentSlugPayloadSchema,
  BrowserLocalReadyEventPayloadSchema,
  BrowserLocalStoppedPayloadSchema,
  BrowserStreamReadyPayloadSchema,
  BrowserStreamStoppedPayloadSchema,
  BrowserTabsUpdatedPayloadSchema,
  ItemCreatedInTaskPayloadSchema,
  OsHandsActivePayloadSchema,
  PromptSuggestionPayloadSchema,
  RoutineCreatedInTaskPayloadSchema,
  RoutineCreatedPayloadSchema,
  RoutineDeletedPayloadSchema,
  RoutineRegisterFailedPayloadSchema,
  RoutineRunCompletedPayloadSchema,
  RoutineRunFailedPayloadSchema,
  RoutineRunStartedPayloadSchema,
  RoutineUpdatedPayloadSchema,
  SandboxStatePayloadSchema,
  SessionStateChangedPayloadSchema,
  UserFileReadyPayloadSchema,
  UserFileSyncedPayloadSchema,
  WorkspaceSyncCompletedPayloadSchema,
  WorkspaceSyncStartedPayloadSchema,
} from '../schemas/forwarded-payloads.schema.js';
import {
  AbilityReloadPayloadSchema,
  AgentReadyPayloadSchema,
  BillingTripwirePayloadSchema,
  ContinueInteractionPayloadSchema,
  ContinueReceivedPayloadSchema,
  CreditBlockedPayloadSchema,
  CreditPermissionResponsePayloadSchema,
  CreditSummaryStalePayloadSchema,
  MessageCompletePayloadSchema,
  MessageSyncRequestPayloadSchema,
  MoveDoorbellPayloadSchema,
  PermissionConfigSetPayloadSchema,
  PermissionRuleRemoveRequestPayloadSchema,
  PermissionRuleRemoveResultPayloadSchema,
  PermissionRulesListRequestPayloadSchema,
  PermissionRulesListResultPayloadSchema,
  PermissionRulesSetPayloadSchema,
  RelayAckPayloadSchema,
  RelayGapPayloadSchema,
  RelayResumePayloadSchema,
  RelayResumedPayloadSchema,
  RunnerDrainPayloadSchema,
  RunnerDrainingPayloadSchema,
  RunnerIdleReleasePayloadSchema,
  SandboxLifecyclePayloadSchema,
  SandboxLogPayloadSchema,
  SandboxStateChangePayloadSchema,
  SetAccessLevelPayloadSchema,
  SetPermissionModePayloadSchema,
  TokenRefreshPayloadSchema,
  ToolBillingPayloadSchema,
  WorkspaceHeadMismatchEventSchema,
  WorkspaceMaterializedEventSchema,
  RunnerRecordHeadRequestSchema,
  RunnerRecordHeadResultSchema,
} from '../schemas/runner-protocol.schema.js';
import {
  AgentNotificationPayloadSchema,
  BrowserCloseSessionPayloadSchema,
  BrowserCloseTabPayloadSchema,
  BrowserOpenSessionPayloadSchema,
  BrowserOpenTabPayloadSchema,
  BrowserSessionStartedPayloadSchema,
  BrowserSessionStoppedPayloadSchema,
  BrowserStatsPayloadSchema,
  BrowserUserInputPayloadSchema,
  BrowserViewStatePayloadSchema,
  BrowserWakePayloadSchema,
  ComputerSoundPayloadSchema,
  ConnectionStateQueryPayloadSchema,
  CreditSummarySchema,
  HookExecutionPayloadSchema,
  JobErrorPayloadSchema,
  JobProgressPayloadSchema,
  JobReadyPayloadSchema,
  NamiCaptureSignalPayloadSchema,
  NamiRememberDepositPayloadSchema,
  NestedToolCallPayloadSchema,
  PresenceSchema,
  SandboxPreWarmPayloadSchema,
  SandboxPrewarmQueryPayloadSchema,
  SandboxTypingPayloadSchema,
  SandboxWarmupQueryReadyPayloadSchema,
  SecurityBlockedPayloadSchema,
  ServerHandoverPayloadSchema,
  TerminalExecPayloadSchema,
  TerminalResultPayloadSchema,
  UserCapabilitiesSchema,
} from '../schemas/client-protocol.schema.js';
import {
  AdminErrorEventPayloadSchema,
  AdminHealthSubscribePayloadSchema,
  AdminIssueCommentAddedPayloadSchema,
  AdminIssueCommentDeletedPayloadSchema,
  AdminIssueCommentUpdatedPayloadSchema,
  AdminIssueCreatedPayloadSchema,
  AdminIssueDeletedPayloadSchema,
  AdminIssueUpdatedPayloadSchema,
  AdminMessageReceivedPayloadSchema,
  SupportAckOnlyPayloadSchema,
  SupportArticleSearchPayloadSchema,
  SupportAssignPayloadSchema,
  SupportAttachmentSignPayloadSchema,
  SupportConversationOpenPayloadSchema,
  SupportConversationUpdatePayloadSchema,
  SupportLinkTaskPayloadSchema,
  SupportMessagePayloadSchema,
  SupportMessageReceivedPayloadSchema,
  SupportMessagesReadPayloadSchema,
  SupportNotePayloadSchema,
  SupportPriorityPayloadSchema,
  SupportRatePayloadSchema,
  SupportResolvePayloadSchema,
  SupportTypingPayloadSchema,
} from '../schemas/support-admin-protocol.schema.js';
import {
  OsAppInventoryFrameSchema,
  OsCaptureRequestSchema,
  OsCaptureResultSchema,
  OsDictateRequestSchema,
  OsDictateResultSchema,
  OsInteractRequestSchema,
  OsInteractResultSchema,
  OsLaneCancelSchema,
  OsLaneProgressSchema,
  OsLaneRequestSchema,
  OsLaneResultSchema,
  OsRecipeCancelSchema,
  OsRecipeRequestSchema,
  OsRecipeResultSchema,
} from '../schemas/os-protocol.schema.js';
import { BoardTaskDeltaSchema } from '../types/board-stream.js';
import { StoryEntryDeltaSchema } from '../types/story.js';
import { LaneStoryPayloadSchema } from '../jev/lane.schema.js';
import {
  CreditApprovalNeededPayloadSchema,
  CreditExhaustedPayloadSchema,
  CreditSyncPayloadSchema,
  CreditToolCheckPayloadSchema,
  CreditToolVerdictPayloadSchema,
  CreditUpdatePayloadSchema,
  CreditWarningPayloadSchema,
} from '../schemas/credit-payloads.schema.js';
import {
  AgentContextUsagePayloadSchema,
  AgentMemoryRecallPayloadSchema,
  AgentUsagePayloadSchema,
  CancelStagedPayloadSchema,
  CheckpointContinuePayloadSchema,
  MessagePickedUpPayloadSchema,
  MessageStagedPayloadSchema,
  StagingSyncPayloadSchema,
  StagingSyncRequestPayloadSchema,
} from '../schemas/staging-payloads.schema.js';
import {
  ConnectionRequiredPayloadSchema,
} from '../schemas/connection-payloads.schema.js';
import { InteractionRequiredEnvelopeSchema } from '../schemas/interaction.schema.js';
import {
  AgentToolUseStartPayloadSchema,
  AgentToolProgressPayloadSchema,
  AgentStatusPayloadSchema,
  SubagentStartPayloadSchema,
  SubagentTextDeltaPayloadSchema,
  SubagentToolUsePayloadSchema,
  SubagentToolResultPayloadSchema,
  SubagentEndPayloadSchema,
} from '../schemas/lane-payloads.schema.js';
import {
  MessageSyncPayloadSchema,
  MessageGapWarningPayloadSchema,
  UserMessageBroadcastPayloadSchema,
} from '../schemas/sync-payloads.schema.js';
import { PurchaseFreezePayloadSchema, PurchaseUpiLinkPayloadSchema } from '../wallet/frames.js';

/**
 * One event's wire contract.
 *  - `forward`: the relay routes this event sandbox→server→frontend (its
 *    membership in {@link FORWARD_EVENTS_SET}). Non-forwarded events are
 *    server-internal or client→server only.
 *  - `schema`: present ⇒ the payload's Zod schema (single author of its shape;
 *    surfaced in {@link EventSchemas}). It is ALSO the payload TYPE: consumers
 *    name `PayloadOf<E>` (events/payloads.ts), which is `z.infer` of this
 *    schema — nothing else authors the shape. Omitted ⇒ schema-less v1 event
 *    (legal; tracked by {@link SCHEMALESS_EVENTS}).
 *  - `from`: which side AUTHORS the frame — the sandbox runner, the server, or
 *    the web client. Stamped as rows get their schema written from the emitter
 *    (s2-07d); absent on rows nobody has audited yet. Direction lives here so the
 *    relay's forward gate is not the only place it is encoded.
 *  - `doc`: optional free-form note. Unused in v1 (rich docs live on EVENTS).
 */
export interface EventContract {
  readonly forward: boolean;
  readonly schema?: z.ZodTypeAny;
  readonly from?: 'sandbox' | 'server' | 'web';
  readonly doc?: string;
}

/**
 * THE authored table. One entry per EVENTS wire name. `satisfies` keeps each
 * entry's precise type (exact schema, so {@link EventSchemas} stays per-key typed)
 * while enforcing exhaustive coverage of every {@link EventName}.
 */
export const EVENT_REGISTRY = {
  [EVENTS.AGENT_REGISTER]: { forward: false, schema: AgentRegisterPayloadSchema },
  [EVENTS.SESSION_CONFIG]: { forward: false, schema: SessionConfigPayloadSchema },
  [EVENTS.USER_MESSAGE]: { forward: false, schema: UserMessagePayloadSchema },
  [EVENTS.USER_MESSAGE_BROADCAST]: { forward: false, schema: UserMessageBroadcastPayloadSchema, from: 'server' },
  [EVENTS.AGENT_READY]: { forward: false, schema: AgentReadyPayloadSchema, from: 'sandbox' },
  [EVENTS.AGENT_START]: { forward: true, schema: AgentStartPayloadSchema },
  [EVENTS.AGENT_END]: { forward: true, schema: AgentEndPayloadSchema },
  [EVENTS.SESSION_STATE_CHANGED]: { forward: true, schema: SessionStateChangedPayloadSchema, from: 'sandbox' },
  [EVENTS.AGENT_ERROR]: { forward: true, schema: AgentErrorPayloadSchema },
  [EVENTS.AGENT_INTERRUPT]: { forward: false, schema: AgentInterruptPayloadSchema },
  [EVENTS.AGENT_USAGE]: { forward: true, schema: AgentUsagePayloadSchema },
  // Off-loop side-call usage (sweep / suggestion). SERVER-CONSUMED — metered into
  // task_turn_usage and nothing else. `forward: false` is load-bearing, not
  // incidental: the web's AGENT_USAGE handler SETS the task's live input count
  // (the desk's tokens) from each event's full context, so a suggestion
  // (history = last user+assistant only) would collapse a 75k reading to ~2k
  // right after every turn.
  [EVENTS.AGENT_OFFLOOP_USAGE]: { forward: false, schema: AgentUsagePayloadSchema },
  [EVENTS.AGENT_CONTEXT_USAGE]: { forward: true, schema: AgentContextUsagePayloadSchema },
  [EVENTS.AGENT_MEMORY_RECALL]: { forward: true, schema: AgentMemoryRecallPayloadSchema },
  [EVENTS.AGENT_TOOL_BILLING]: { forward: false, schema: ToolBillingPayloadSchema, from: 'sandbox' },
  // Engine billing-keystone violation. SERVER-CONSUMED (persisted as an error_event
  // by the relay handler) — never forwarded to the frontend, like NAMI_CAPTURE_SIGNAL.
  [EVENTS.AGENT_BILLING_TRIPWIRE]: { forward: false, schema: BillingTripwirePayloadSchema, from: 'sandbox' },
  // Engine wire-vs-journal reconstruction desync. SERVER-CONSUMED (persisted as an
  // error_event by the relay handler) — never forwarded, same rail as the tripwire.
  [EVENTS.AGENT_RECONSTRUCTION_MISMATCH]: { forward: false, schema: AgentReconstructionMismatchPayloadSchema },
  // Engine rejected-history heal (live 28edef30). SERVER-CONSUMED (persisted as an
  // error_event by the relay handler), never forwarded, same rail as the two above.
  [EVENTS.AGENT_HISTORY_HEALED]: { forward: false, schema: AgentHistoryHealedPayloadSchema },
  // X1 Stop-hook continuation (HOOK_PARITY_V1): each extra billed round a hook forced.
  // SERVER-CONSUMED (persisted as an error_event), never forwarded, same rail.
  [EVENTS.AGENT_HOOK_STOP_CONTINUED]: { forward: false, schema: AgentHookStopContinuedPayloadSchema },
  // A native model stream the stall guard declared dead, re-issued or terminal. SERVER-CONSUMED
  // (persisted as an error_event), never forwarded, same rail.
  [EVENTS.AGENT_STREAM_STALLED]: { forward: false, schema: AgentStreamStalledPayloadSchema },
  // OTEL_TRACES_V1: the runner's finished spans for a sampled turn. SERVER-CONSUMED (recorded and
  // exported by the server; the box never exports), never forwarded.
  [EVENTS.AGENT_SPANS]: { forward: false, schema: AgentSpansPayloadSchema },
  [EVENTS.SANDBOX_LOG]: { forward: false, schema: SandboxLogPayloadSchema, from: 'sandbox' },
  // The box's own file-change report — consumed by the relay, never a UI event.
  [EVENTS.FILES_CHANGED]: { forward: false, schema: FilesChangedPayloadSchema },
  [EVENTS.SYSTEM_INIT]: { forward: false, schema: SystemInitPayloadSchema },
  [EVENTS.AGENT_RESULT]: { forward: true, schema: AgentResultPayloadSchema },
  [EVENTS.AGENT_THINKING]: { forward: true, schema: AgentThinkingPayloadSchema },
  [EVENTS.AGENT_THINKING_DELTA]: { forward: true, schema: AgentThinkingDeltaPayloadSchema },
  [EVENTS.AGENT_THINKING_START]: { forward: true, schema: AgentThinkingStartPayloadSchema },
  [EVENTS.AGENT_TEXT]: { forward: true, schema: AgentTextPayloadSchema },
  [EVENTS.AGENT_TEXT_DELTA]: { forward: true, schema: AgentTextDeltaPayloadSchema },
  [EVENTS.AGENT_TOOL_USE]: { forward: true, schema: AgentToolUsePayloadSchema },
  [EVENTS.AGENT_TOOL_RESULT]: { forward: true, schema: AgentToolResultPayloadSchema },
  [EVENTS.AGENT_NESTED_TOOL_CALL]: { forward: false, schema: NestedToolCallPayloadSchema, from: 'sandbox' },
  [EVENTS.JOB_READY]: { forward: false, schema: JobReadyPayloadSchema, from: 'server' },
  [EVENTS.JOB_ERROR]: { forward: false, schema: JobErrorPayloadSchema, from: 'server' },
  [EVENTS.JOB_PROGRESS]: { forward: false, schema: JobProgressPayloadSchema, from: 'server' },
  [EVENTS.AGENT_TODO]: { forward: true, schema: AgentTodoPayloadSchema },
  // The gate parses the ENVELOPE (request by discriminator only) — never the kind
  // union, which would drop server-minted hands_approval asks (interaction.schema.ts).
  [EVENTS.INTERACTION_REQUIRED]: { forward: true, schema: InteractionRequiredEnvelopeSchema, from: 'sandbox' },
  [EVENTS.CONTINUE_INTERACTION]: { forward: false, schema: ContinueInteractionPayloadSchema, from: 'server' },
  [EVENTS.CONTINUE_RECEIVED]: { forward: false, schema: ContinueReceivedPayloadSchema, from: 'sandbox' },
  [EVENTS.MESSAGE_STAGED]: { forward: true, schema: MessageStagedPayloadSchema },
  [EVENTS.MESSAGE_PICKED_UP]: { forward: true, schema: MessagePickedUpPayloadSchema },
  [EVENTS.CANCEL_STAGED]: { forward: false, schema: CancelStagedPayloadSchema },
  [EVENTS.STAGING_SYNC_REQUEST]: { forward: false, schema: StagingSyncRequestPayloadSchema },
  [EVENTS.STAGING_SYNC]: { forward: true, schema: StagingSyncPayloadSchema },
  [EVENTS.SET_PERMISSION_MODE]: { forward: false, schema: SetPermissionModePayloadSchema },
  [EVENTS.SET_ACCESS_LEVEL]: { forward: false, schema: SetAccessLevelPayloadSchema },
  // Permissions-page grants channel (A3): request/reply over the relay, resolved by
  // requestId. Server-internal — never forwarded sandbox→frontend.
  [EVENTS.PERMISSION_RULES_LIST_REQUEST]: { forward: false, schema: PermissionRulesListRequestPayloadSchema, from: 'server' },
  [EVENTS.PERMISSION_RULES_LIST_RESULT]: { forward: false, schema: PermissionRulesListResultPayloadSchema, from: 'sandbox' },
  [EVENTS.PERMISSION_RULE_REMOVE_REQUEST]: { forward: false, schema: PermissionRuleRemoveRequestPayloadSchema, from: 'server' },
  [EVENTS.PERMISSION_RULE_REMOVE_RESULT]: { forward: false, schema: PermissionRuleRemoveResultPayloadSchema, from: 'sandbox' },
  // Task history (R3 fork, R4b rewind): request/reply over the relay, resolved by requestId. Server-internal.
  [EVENTS.TASK_FORK_REQUEST]: { forward: false, schema: TaskForkRequestPayloadSchema },
  [EVENTS.TASK_FORK_RESULT]: { forward: false, schema: TaskForkResultPayloadSchema },
  [EVENTS.TASK_REWIND_REQUEST]: { forward: false, schema: TaskRewindRequestPayloadSchema },
  [EVENTS.TASK_REWIND_RESULT]: { forward: false, schema: TaskRewindResultPayloadSchema },
  [EVENTS.PERMISSION_RULES_SET]: { forward: false, schema: PermissionRulesSetPayloadSchema, from: 'server' },
  [EVENTS.PERMISSION_CONFIG_SET]: { forward: false, schema: PermissionConfigSetPayloadSchema, from: 'server' },
  // The gate's refusal. The relay composes the person's line onto it (`notice`) before the
  // forward, so the web renders the same line the turn persists.
  [EVENTS.PERMISSION_DENIED]: { forward: true, schema: PermissionDeniedPayloadSchema },
  // One quiet line for the person in a task: buffered by the relay, then forwarded.
  [EVENTS.TASK_NOTICE]: { forward: true, schema: TaskNoticePayloadSchema },
  [EVENTS.TASK_CREATED]: { forward: true, schema: TaskCreatedPayloadSchema, from: 'server' },
  [EVENTS.TASK_RESUME]: { forward: false, schema: TaskResumePayloadSchema },
  [EVENTS.TASK_RESUMED]: { forward: true, schema: TaskResumedPayloadSchema, from: 'server' },
  [EVENTS.TASK_LIST]: { forward: false, schema: TaskListPayloadSchema },
  [EVENTS.TASK_UPDATE]: { forward: true, schema: TaskUpdatePayloadSchema, from: 'server' },
  [EVENTS.BOARD_TASK_DELTA]: { forward: false, schema: BoardTaskDeltaSchema },
  [EVENTS.STORY_ENTRY]: { forward: false, schema: StoryEntryDeltaSchema },
  [EVENTS.TASK_DESTROY]: { forward: false, schema: TaskDestroyPayloadSchema },
  [EVENTS.TASK_TITLE_EDIT]: { forward: false, schema: TaskTitleEditPayloadSchema },
  [EVENTS.TASK_OUTPUT_REGISTERED]: { forward: true, schema: TaskOutputRegisteredPayloadSchema, from: 'server' },
  [EVENTS.TASK_ARTIFACT_REGISTERED]: { forward: true, schema: TaskArtifactRegisteredPayloadSchema },
  [EVENTS.TASK_PREVIEW_READY]: { forward: true, schema: TaskPreviewReadyPayloadSchema },
  [EVENTS.TASK_PREVIEW_CLOSED]: { forward: true, schema: TaskPreviewClosedPayloadSchema },
  [EVENTS.APPS_CHANGED]: { forward: true, schema: AppsChangedPayloadSchema },
  [EVENTS.APPS_VIEW]: { forward: false, schema: AppsViewPayloadSchema },
  [EVENTS.TASK_RESUME_FAILED]: { forward: false, schema: TaskResumeFailedPayloadSchema },
  [EVENTS.TASK_STATS]: { forward: false, schema: TaskStatsPayloadSchema },
  [EVENTS.TASK_STREAM]: { forward: false, schema: TaskStreamPayloadSchema, from: 'server' },
  [EVENTS.SESSION_CREATE]: { forward: false, schema: SessionCreatePayloadSchema },
  [EVENTS.SESSION_CREATED]: { forward: false, schema: SessionCreatedPayloadSchema },
  [EVENTS.SESSION_LIST]: { forward: false, schema: SessionListPayloadSchema },
  [EVENTS.SESSION_RESUME]: { forward: false, schema: SessionResumePayloadSchema },
  [EVENTS.SESSION_DESTROY]: { forward: false, schema: SessionDestroyPayloadSchema },
  [EVENTS.SESSION_UPDATE]: { forward: false, schema: SessionUpdatePayloadSchema },
  [EVENTS.SANDBOX_STATE]: { forward: true, schema: SandboxStatePayloadSchema, from: 'sandbox' },
  [EVENTS.CONNECT]: { forward: false },
  [EVENTS.DISCONNECT]: { forward: false },
  [EVENTS.CONNECTION_ERROR]: { forward: false },
  // Zero-gap deploys: the old server's last word to its OWN tabs before it hands them over.
  [EVENTS.SERVER_HANDOVER]: { forward: false, schema: ServerHandoverPayloadSchema, from: 'server' },
  [EVENTS.SECURITY_BLOCKED]: { forward: false, schema: SecurityBlockedPayloadSchema, from: 'sandbox' },
  [EVENTS.AGENT_NOTIFICATION]: { forward: false, schema: AgentNotificationPayloadSchema, from: 'sandbox' },
  [EVENTS.AGENT_HOOK_EXECUTION]: { forward: false, schema: HookExecutionPayloadSchema, from: 'sandbox' },
  [EVENTS.MESSAGE_SYNC_REQUEST]: { forward: false, schema: MessageSyncRequestPayloadSchema, from: 'web' },
  [EVENTS.MESSAGE_SYNC]: { forward: true, schema: MessageSyncPayloadSchema, from: 'server' },
  // Server-originated (emitToUser) — never travels sandbox→frontend, so forward:false.
  [EVENTS.TASK_SYNC_HINT]: { forward: false, schema: TaskSyncHintPayloadSchema },
  // Server-originated (emitToUser) — the W4-2 shadow screen banner; forward:false.
  [EVENTS.INJECTION_SCREEN_NOTICE]: { forward: false, schema: InjectionScreenNoticePayloadSchema },
  [EVENTS.MESSAGE_COMPLETE]: { forward: false, schema: MessageCompletePayloadSchema, from: 'sandbox' },
  [EVENTS.PRESENCE_UPDATE]: { forward: false, schema: PresenceSchema, from: 'server' },
  [EVENTS.SANDBOX_IDLE]: { forward: false, schema: SandboxLifecyclePayloadSchema, from: 'sandbox' },
  [EVENTS.SANDBOX_ACTIVE]: { forward: false, schema: SandboxLifecyclePayloadSchema, from: 'sandbox' },
  [EVENTS.SANDBOX_STATE_CHANGE]: { forward: false, schema: SandboxStateChangePayloadSchema, from: 'sandbox' },
  [EVENTS.CONNECTION_REQUIRED]: { forward: false, schema: ConnectionRequiredPayloadSchema },
  [EVENTS.CHECKPOINT_CONTINUE]: { forward: false, schema: CheckpointContinuePayloadSchema, from: 'web' },
  [EVENTS.CONNECTION_STATE_QUERY]: { forward: false, schema: ConnectionStateQueryPayloadSchema, from: 'web' },
  [EVENTS.CONNECTION_HEARTBEAT]: { forward: false, schema: ConnectionHeartbeatPayloadSchema },
  [EVENTS.SANDBOX_PRE_WARM]: { forward: false, schema: SandboxPreWarmPayloadSchema, from: 'web' },
  [EVENTS.SANDBOX_TYPING]: { forward: false, schema: SandboxTypingPayloadSchema },
  [EVENTS.PREWARM_QUERY]: { forward: false, schema: SandboxPrewarmQueryPayloadSchema, from: 'server' },
  [EVENTS.WARMUP_QUERY_READY]: { forward: false, schema: SandboxWarmupQueryReadyPayloadSchema, from: 'sandbox' },
  [EVENTS.MESSAGE_GAP_WARNING]: { forward: false, schema: MessageGapWarningPayloadSchema, from: 'server' },
  [EVENTS.AGENT_INSTALLED]: { forward: true, schema: AgentInstalledPayloadSchema, from: 'server' },
  [EVENTS.AGENT_UNINSTALLED]: { forward: true, schema: AgentSlugPayloadSchema, from: 'server' },
  [EVENTS.AGENT_PUBLISHED]: { forward: true, schema: AgentSlugPayloadSchema, from: 'server' },
  [EVENTS.AGENT_UNPUBLISHED]: { forward: true, schema: AgentSlugPayloadSchema, from: 'server' },
  [EVENTS.AGENT_CREATED]: { forward: true, schema: AgentCrudPayloadSchema, from: 'server' },
  [EVENTS.AGENT_UPDATED]: { forward: true, schema: AgentCrudPayloadSchema, from: 'server' },
  [EVENTS.AGENT_DELETED]: { forward: true, schema: AgentSlugPayloadSchema, from: 'server' },
  [EVENTS.SUBAGENT_START]: { forward: true, schema: SubagentStartPayloadSchema, from: 'sandbox' },
  [EVENTS.SUBAGENT_TEXT_DELTA]: { forward: true, schema: SubagentTextDeltaPayloadSchema, from: 'sandbox' },
  [EVENTS.SUBAGENT_TOOL_USE]: { forward: true, schema: SubagentToolUsePayloadSchema, from: 'sandbox' },
  [EVENTS.SUBAGENT_TOOL_RESULT]: { forward: true, schema: SubagentToolResultPayloadSchema, from: 'sandbox' },
  [EVENTS.SUBAGENT_END]: { forward: true, schema: SubagentEndPayloadSchema, from: 'sandbox' },
  [EVENTS.SUBAGENT_SPAWNED]: { forward: true, schema: SubagentSpawnedPayloadSchema },
  [EVENTS.SUBAGENT_MESSAGE]: { forward: true, schema: SubagentMessagePayloadSchema },
  [EVENTS.AGENT_TOOL_USE_START]: { forward: true, schema: AgentToolUseStartPayloadSchema, from: 'sandbox' },
  [EVENTS.AGENT_TOOL_PROGRESS]: { forward: true, schema: AgentToolProgressPayloadSchema, from: 'sandbox' },
  // Minted, not forwarded: the server writes the row and the row pushes itself
  // (story:entry). Forwarding it too would put the same line on the web twice.
  [EVENTS.LANE_STORY]: { forward: false, schema: LaneStoryPayloadSchema },
  [EVENTS.AGENT_STATUS]: { forward: true, schema: AgentStatusPayloadSchema, from: 'sandbox' },
  [EVENTS.SESSION_COMPACTED]: { forward: true, schema: SessionCompactedPayloadSchema },
  [EVENTS.MESSAGE_CREDIT_UPDATE]: { forward: false, schema: MessageCreditUpdatePayloadSchema },
  [EVENTS.WORKSPACE_SYNC_STARTED]: { forward: true, schema: WorkspaceSyncStartedPayloadSchema, from: 'server' },
  [EVENTS.WORKSPACE_SYNC_COMPLETED]: { forward: true, schema: WorkspaceSyncCompletedPayloadSchema, from: 'server' },
  [EVENTS.USER_FILE_SYNCED]: { forward: true, schema: UserFileSyncedPayloadSchema, from: 'server' },
  [EVENTS.USER_FILE_READY]: { forward: true, schema: UserFileReadyPayloadSchema, from: 'server' },
  // forward: false, like NAMI_NOTIFY: the server emits these straight into the
  // user's room (no sandbox leg), so the relay gate must never admit them.
  [EVENTS.COMPOSER_LIVE_STATE]: { forward: false, schema: LiveStatePayloadSchema },
  [EVENTS.COMPOSER_LIVE_EVENT]: { forward: false, schema: LiveEventPayloadSchema },
  // Browser -> server only, like COMPOSER_SCREEN: read by the line actor's idle
  // policy, never relayed to a sandbox and never emitted back down.
  [EVENTS.COMPOSER_LINE_MUTE]: { forward: false, schema: LineMutePayloadSchema },
  // Browser → server only (spec 2026-09-18, R-L1): kept on the socket, read by the rung chooser.
  // No sandbox leg and nothing emitted back, so the relay gate must never admit it.
  [EVENTS.COMPOSER_SCREEN]: { forward: false, schema: ComposerScreenPayloadSchema },
  [EVENTS.CREDIT_UPDATE]: { forward: true, schema: CreditUpdatePayloadSchema },
  [EVENTS.CREDIT_WARNING]: { forward: true, schema: CreditWarningPayloadSchema },
  [EVENTS.CREDIT_EXHAUSTED]: { forward: true, schema: CreditExhaustedPayloadSchema },
  [EVENTS.CREDIT_PERMISSION_RESPONSE]: { forward: false, schema: CreditPermissionResponsePayloadSchema, from: 'web' },
  [EVENTS.CREDIT_SUMMARY]: { forward: false, schema: CreditSummarySchema, from: 'server' },
  [EVENTS.CREDIT_SUMMARY_STALE]: { forward: false, schema: CreditSummaryStalePayloadSchema, from: 'server' },
  [EVENTS.CREDIT_SYNC]: { forward: false, schema: CreditSyncPayloadSchema },
  [EVENTS.TOKEN_REFRESH]: { forward: false, schema: TokenRefreshPayloadSchema, from: 'server' },
  [EVENTS.ABILITY_RELOAD]: { forward: false, schema: AbilityReloadPayloadSchema, from: 'server' },
  [EVENTS.RUNNER_DRAIN]: { forward: false, schema: RunnerDrainPayloadSchema, from: 'server' },
  [EVENTS.RUNNER_DRAINING]: { forward: false, schema: RunnerDrainingPayloadSchema, from: 'sandbox' },
  [EVENTS.RUNNER_STATUS]: { forward: false, schema: RunnerStatusPayloadSchema },
  [EVENTS.MOVE_DOORBELL]: { forward: false, schema: MoveDoorbellPayloadSchema, from: 'server' },
  [EVENTS.RUNNER_IDLE_RELEASE]: { forward: false, schema: RunnerIdleReleasePayloadSchema, from: 'server' },
  [EVENTS.WORKSPACE_MATERIALIZED]: { forward: false, schema: WorkspaceMaterializedEventSchema, from: 'sandbox' },
  [EVENTS.WORKSPACE_HEAD_MISMATCH]: { forward: false, schema: WorkspaceHeadMismatchEventSchema, from: 'sandbox' },
  // Save now (box hosts, Plan 4): server → runner, and the runner's one answer (awaitSandboxEvent). The answer
  // names no task, so the relay's destroyed-task guard never judges it (relay/destroyed-guard.ts). Never forwarded.
  [EVENTS.RUNNER_RECORD_HEAD]: { forward: false, schema: RunnerRecordHeadRequestSchema, from: 'server' },
  [EVENTS.RUNNER_RECORD_HEAD_RESULT]: { forward: false, schema: RunnerRecordHeadResultSchema, from: 'sandbox' },
  // Turn reattach rail — runner↔server control frames. Never forwarded: the
  // frontend replays a turn from the server's record, never from these.
  [EVENTS.RELAY_RESUME]: { forward: false, schema: RelayResumePayloadSchema, from: 'server' },
  [EVENTS.RELAY_RESUMED]: { forward: false, schema: RelayResumedPayloadSchema, from: 'sandbox' },
  [EVENTS.RELAY_ACK]: { forward: false, schema: RelayAckPayloadSchema, from: 'server' },
  [EVENTS.RELAY_GAP]: { forward: false, schema: RelayGapPayloadSchema, from: 'sandbox' },
  [EVENTS.CREDIT_BLOCKED]: { forward: false, schema: CreditBlockedPayloadSchema, from: 'sandbox' },
  [EVENTS.CREDIT_TOOL_CHECK]: { forward: false, schema: CreditToolCheckPayloadSchema },
  [EVENTS.CREDIT_TOOL_VERDICT]: { forward: false, schema: CreditToolVerdictPayloadSchema },
  [EVENTS.CREDIT_APPROVAL_NEEDED]: { forward: true, schema: CreditApprovalNeededPayloadSchema },
  [EVENTS.DEPOSIT_ACK]: { forward: false, schema: DepositAckPayloadSchema },
  [EVENTS.SUPPORT_CONVERSATION_OPEN]: { forward: false, schema: SupportConversationOpenPayloadSchema, from: 'web' },
  [EVENTS.SUPPORT_MESSAGE_SEND]: { forward: false, schema: SupportMessagePayloadSchema, from: 'web' },
  [EVENTS.SUPPORT_TYPING_START]: { forward: false, schema: SupportTypingPayloadSchema },
  [EVENTS.SUPPORT_TYPING_STOP]: { forward: false, schema: SupportTypingPayloadSchema },
  [EVENTS.SUPPORT_CONVERSATION_LIST]: { forward: false, schema: SupportAckOnlyPayloadSchema, from: 'web' },
  [EVENTS.SUPPORT_ARTICLE_SEARCH]: { forward: false, schema: SupportArticleSearchPayloadSchema, from: 'web' },
  [EVENTS.SUPPORT_MESSAGE_RECEIVED]: { forward: false, schema: SupportMessageReceivedPayloadSchema, from: 'server' },
  [EVENTS.SUPPORT_CONVERSATION_UPDATE]: { forward: false, schema: SupportConversationUpdatePayloadSchema, from: 'server' },
  [EVENTS.SUPPORT_REPLY_SEND]: { forward: false, schema: SupportMessagePayloadSchema, from: 'web' },
  [EVENTS.SUPPORT_NOTE_SEND]: { forward: false, schema: SupportNotePayloadSchema, from: 'web' },
  [EVENTS.SUPPORT_ASSIGN]: { forward: false, schema: SupportAssignPayloadSchema, from: 'web' },
  [EVENTS.SUPPORT_RESOLVE]: { forward: false, schema: SupportResolvePayloadSchema, from: 'web' },
  [EVENTS.SUPPORT_PRIORITY_UPDATE]: { forward: false, schema: SupportPriorityPayloadSchema, from: 'web' },
  [EVENTS.SUPPORT_INBOX_SUBSCRIBE]: { forward: false, schema: SupportAckOnlyPayloadSchema, from: 'web' },
  [EVENTS.SUPPORT_NEW_CONVERSATION]: { forward: false, schema: SupportConversationUpdatePayloadSchema, from: 'server' },
  [EVENTS.SUPPORT_MESSAGE_NEW]: { forward: false, schema: SupportMessageReceivedPayloadSchema, from: 'server' },
  [EVENTS.SUPPORT_AGENT_ASSIGNED]: { forward: false, schema: SupportConversationUpdatePayloadSchema, from: 'server' },
  [EVENTS.SUPPORT_CUSTOMER_RESOLVE]: { forward: false, schema: SupportResolvePayloadSchema, from: 'web' },
  [EVENTS.SUPPORT_MESSAGES_READ]: { forward: false, schema: SupportMessagesReadPayloadSchema, from: 'server' },
  [EVENTS.SUPPORT_RECENT_TASKS]: { forward: false, schema: SupportAckOnlyPayloadSchema, from: 'web' },
  [EVENTS.SUPPORT_ATTACHMENT_SIGN]: { forward: false, schema: SupportAttachmentSignPayloadSchema, from: 'web' },
  [EVENTS.SUPPORT_RATE]: { forward: false, schema: SupportRatePayloadSchema, from: 'web' },
  [EVENTS.SUPPORT_LINK_TASK]: { forward: false, schema: SupportLinkTaskPayloadSchema, from: 'web' },
  [EVENTS.ROUTINE_CREATED]: { forward: true, schema: RoutineCreatedPayloadSchema, from: 'server' },
  [EVENTS.ROUTINE_UPDATED]: { forward: true, schema: RoutineUpdatedPayloadSchema, from: 'server' },
  [EVENTS.ROUTINE_DELETED]: { forward: true, schema: RoutineDeletedPayloadSchema, from: 'server' },
  [EVENTS.ROUTINE_RUN_STARTED]: { forward: true, schema: RoutineRunStartedPayloadSchema, from: 'server' },
  [EVENTS.ROUTINE_RUN_COMPLETED]: { forward: true, schema: RoutineRunCompletedPayloadSchema, from: 'server' },
  [EVENTS.ROUTINE_RUN_FAILED]: { forward: true, schema: RoutineRunFailedPayloadSchema, from: 'server' },
  [EVENTS.ROUTINE_REGISTER_FAILED]: { forward: true, schema: RoutineRegisterFailedPayloadSchema, from: 'server' },
  [EVENTS.ROUTINE_CREATED_IN_TASK]: { forward: true, schema: RoutineCreatedInTaskPayloadSchema, from: 'sandbox' },
  [EVENTS.ROUTINE_COMPLETED]: { forward: true, schema: RoutineRunCompletedPayloadSchema, from: 'server' },
  [EVENTS.ITEM_CREATED_IN_TASK]: { forward: true, schema: ItemCreatedInTaskPayloadSchema, from: 'sandbox' },
  [EVENTS.PROMPT_SUGGESTION]: { forward: true, schema: PromptSuggestionPayloadSchema, from: 'sandbox' },
  [EVENTS.NAMI_CAPTURE_SIGNAL]: { forward: false, schema: NamiCaptureSignalPayloadSchema, from: 'sandbox' },
  [EVENTS.NAMI_REMEMBER_DEPOSIT]: { forward: false, schema: NamiRememberDepositPayloadSchema, from: 'sandbox' },
  // forward: false is load-bearing: the snapshot is the agent's private craft, read by the
  // server for the next session's prompt. The web has no use for it.
  [EVENTS.AGENT_NOTEBOOK]: { forward: false, schema: AgentNotebookPayloadSchema },
  // forward: false is load-bearing, not incidental — FORWARD_EVENTS is DERIVED
  // from this flag, and the sweep terminator is server-consumed (it drives the
  // fold). The frontend has no use for it and must never see it.
  [EVENTS.NAMI_SWEEP_COMPLETE]: { forward: false, schema: NamiSweepCompletePayloadSchema },
  // forward: false because FORWARD_EVENTS is the sandbox→frontend relay gate and
  // this frame has no sandbox leg: the server emits it straight into the user's
  // room via emitToUser. The frontend DOES receive it; it just never travels the
  // relay path, so listing it here would allow a runner to forge a reach frame.
  [EVENTS.NAMI_NOTIFY]: { forward: false, schema: NamiNotifyPayloadSchema },
  [EVENTS.ADMIN_HEALTH_SUBSCRIBE]: { forward: false, schema: AdminHealthSubscribePayloadSchema, from: 'web' },
  [EVENTS.ADMIN_ERROR_EVENT]: { forward: false, schema: AdminErrorEventPayloadSchema, from: 'server' },
  [EVENTS.ADMIN_MESSAGE_RECEIVED]: { forward: false, schema: AdminMessageReceivedPayloadSchema, from: 'server' },
  [EVENTS.ADMIN_ISSUE_CREATED]: { forward: false, schema: AdminIssueCreatedPayloadSchema, from: 'server' },
  [EVENTS.ADMIN_ISSUE_UPDATED]: { forward: false, schema: AdminIssueUpdatedPayloadSchema, from: 'server' },
  [EVENTS.ADMIN_ISSUE_DELETED]: { forward: false, schema: AdminIssueDeletedPayloadSchema, from: 'server' },
  [EVENTS.ADMIN_ISSUE_COMMENT_ADDED]: { forward: false, schema: AdminIssueCommentAddedPayloadSchema, from: 'server' },
  [EVENTS.ADMIN_ISSUE_COMMENT_UPDATED]: { forward: false, schema: AdminIssueCommentUpdatedPayloadSchema, from: 'server' },
  [EVENTS.ADMIN_ISSUE_COMMENT_DELETED]: { forward: false, schema: AdminIssueCommentDeletedPayloadSchema, from: 'server' },
  [EVENTS.USER_CAPABILITIES]: { forward: false, schema: UserCapabilitiesSchema, from: 'server' },
  [EVENTS.BROWSER_SESSION_STARTED]: { forward: false, schema: BrowserSessionStartedPayloadSchema, from: 'sandbox' },
  [EVENTS.BROWSER_SESSION_STOPPED]: { forward: false, schema: BrowserSessionStoppedPayloadSchema, from: 'sandbox' },
  [EVENTS.BROWSER_STREAM_READY]: { forward: true, schema: BrowserStreamReadyPayloadSchema, from: 'server' },
  [EVENTS.BROWSER_STREAM_STOPPED]: { forward: true, schema: BrowserStreamStoppedPayloadSchema, from: 'server' },
  // BROWSER_VIEW_MACHINE_V1 — server-owned view snapshot (server→frontend,
  // server-side handler emits directly, never sandbox-relayed) and the
  // explicit Wake demand (frontend→server, relay never forwards it back).
  [EVENTS.BROWSER_VIEW_STATE]: { forward: false, schema: BrowserViewStatePayloadSchema, from: 'server' },
  // The view PULL (frontend→server); answered by the server itself, so the
  // relay never forwards it to the sandbox.
  [EVENTS.BROWSER_VIEW_SYNC]: { forward: false, schema: BrowserViewSyncPayloadSchema },
  [EVENTS.BROWSER_WAKE]: { forward: false, schema: BrowserWakePayloadSchema, from: 'web' },
  // Server → sandbox only (grant carrier for a computer-level wake); the relay
  // never forwards it sandbox→frontend.
  [EVENTS.COMPUTER_WAKE]: { forward: false, schema: ComputerWakePayloadSchema },
  // Frontend → server → sandbox (the human's sleep); the relay never forwards
  // it sandbox→frontend.
  [EVENTS.COMPUTER_SLEEP]: { forward: false, schema: ComputerSleepPayloadSchema },
  // Frontend → server ONLY (the sound beat); the server holds the box on it and
  // terminates it there.
  [EVENTS.COMPUTER_SOUND]: { forward: false, schema: ComputerSoundPayloadSchema, from: 'web' },
  // The agent's computer-view verbs — sandbox → frontend, relay-forwarded.
  [EVENTS.COMPUTER_SHOW]: { forward: true, schema: ComputerShowPayloadSchema },
  [EVENTS.COMPUTER_CLOSE]: { forward: true, schema: ComputerClosePayloadSchema },
  [EVENTS.BROWSER_TABS_UPDATED]: { forward: true, schema: BrowserTabsUpdatedPayloadSchema, from: 'sandbox' },
  // Embedded browser chrome tab strip — sandbox→server→frontend snapshots
  // (local/embedded lane only).
  [EVENTS.BROWSER_TABS]: { forward: true, schema: BrowserTabsPayloadSchema },
  [EVENTS.BROWSER_OPEN_TAB]: { forward: false, schema: BrowserOpenTabPayloadSchema },
  [EVENTS.BROWSER_CLOSE_TAB]: { forward: false, schema: BrowserCloseTabPayloadSchema },
  [EVENTS.BROWSER_OPEN_SESSION]: { forward: false, schema: BrowserOpenSessionPayloadSchema },
  [EVENTS.BROWSER_CLOSE_SESSION]: { forward: false, schema: BrowserCloseSessionPayloadSchema, from: 'web' },
  [EVENTS.BROWSER_STATS]: { forward: false, schema: BrowserStatsPayloadSchema, from: 'web' },
  [EVENTS.BROWSER_USER_INPUT]: { forward: false, schema: BrowserUserInputPayloadSchema },
  // Pane visibility (work holds the box, spec §5.1) — frontend→server ONLY. The
  // server drops the viewer hold on `false` and terminates the event there: it
  // reaches neither the sandbox nor, forwarded back, the frontend.
  [EVENTS.BROWSER_PANE_VISIBILITY]: {
    forward: false,
    schema: BrowserPaneVisibilityPayloadSchema,
  },
  // Embedded browser chrome navigation — frontend→server→sandbox command
  // channel; the relay never forwards it back to the frontend.
  [EVENTS.BROWSER_NAVIGATE]: { forward: false, schema: BrowserNavigatePayloadSchema },
  [EVENTS.BROWSER_LOCAL_READY]: { forward: true, schema: BrowserLocalReadyEventPayloadSchema, from: 'sandbox' },
  [EVENTS.BROWSER_LOCAL_STOPPED]: { forward: true, schema: BrowserLocalStoppedPayloadSchema, from: 'sandbox' },
  // The live view's presence beat (frontend → server ONLY; the server notes it
  // for the awake guard and terminates it there). SUSPENDED/RESUMED are
  // server-emitted, never relayed from the sandbox. All three stay off the
  // relay's forward path.
  [EVENTS.BROWSER_VIEWING]: { forward: false, schema: BrowserViewingPayloadSchema, from: 'web' },
  [EVENTS.BROWSER_STREAM_SUSPENDED]: { forward: false, schema: BrowserStreamSuspendedPayloadSchema },
  [EVENTS.BROWSER_STREAM_RESUMED]: { forward: false, schema: BrowserStreamResumedPayloadSchema },
  // Cobrowsing P3 telemetry ([ow1]) — sandbox→server deferral signal, consumed
  // by the relay's cobrowse-telemetry delegate; never forwarded to the frontend.
  [EVENTS.BROWSER_CONTROL_DEFERRED]: { forward: false, schema: BrowserControlDeferredPayloadSchema },
  // BROWSER_MIRROR_V1 — sandbox→server batched rrweb events. forward:false: the
  // server owns a dedicated buffered fan-out handler (ring buffer + replay to
  // late-joining panels), never the generic relay forward (like BROWSER_VIEWING).
  [EVENTS.BROWSER_MIRROR_EVENT]: { forward: false, schema: BrowserMirrorEventPayloadSchema },
  // BROWSER_MIRROR_V1 — server→sandbox resnapshot request (takeFullSnapshot). Never
  // forwarded to the frontend; the sandbox handles it directly (no-op unless armed).
  [EVENTS.BROWSER_MIRROR_RESNAPSHOT]: { forward: false, schema: BrowserMirrorResnapshotPayloadSchema },
  // Browser fetch: server → runner, and the runner's one answer (awaitSandboxEvent). Never forwarded.
  [EVENTS.BROWSER_FETCH_REQUEST]: { forward: false, schema: BrowserFetchRequestPayloadSchema, from: 'server' },
  [EVENTS.BROWSER_FETCH_RESULT]: { forward: false, schema: BrowserFetchResultPayloadSchema, from: 'sandbox' },
  // Browser cookies: server → runner, and the runner's one answer (awaitSandboxEvent). Names and expiry only. Never forwarded.
  [EVENTS.BROWSER_COOKIES_REQUEST]: { forward: false, schema: BrowserCookiesRequestPayloadSchema, from: 'server' },
  [EVENTS.BROWSER_COOKIES_RESULT]: { forward: false, schema: BrowserCookiesResultPayloadSchema, from: 'sandbox' },
  // The box door (box hosts spec §6): server → runner, and the runner's one answer per request (awaitSandboxEvent). Never forwarded.
  [EVENTS.BOX_FS_REQUEST]: { forward: false, schema: BoxFsRequestPayloadSchema, from: 'server' },
  [EVENTS.BOX_FS_RESULT]: { forward: false, schema: BoxFsResultPayloadSchema, from: 'sandbox' },
  [EVENTS.BOX_EXEC_REQUEST]: { forward: false, schema: BoxExecRequestPayloadSchema, from: 'server' },
  [EVENTS.BOX_EXEC_RESULT]: { forward: false, schema: BoxExecResultPayloadSchema, from: 'sandbox' },
  [EVENTS.BOX_LOGS_REQUEST]: { forward: false, schema: BoxLogsRequestPayloadSchema, from: 'server' },
  [EVENTS.BOX_LOGS_RESULT]: { forward: false, schema: BoxLogsResultPayloadSchema, from: 'sandbox' },
  [EVENTS.BROWSER_WATCH_START]: { forward: false, schema: BrowserWatchStartPayloadSchema, from: 'server' },
  [EVENTS.BROWSER_WATCH_STOP]: { forward: false, schema: BrowserWatchStopPayloadSchema, from: 'server' },
  [EVENTS.BROWSER_WATCH_EVENT]: { forward: false, schema: BrowserWatchEventPayloadSchema, from: 'sandbox' },
  // OS-hands ambient signal — sandbox→web forward-first (schemaless like its
  // browser-lane neighbors; payload = OsHandsActivePayload).
  [EVENTS.OS_HANDS_ACTIVE]: { forward: true, schema: OsHandsActivePayloadSchema, from: 'sandbox' },
  // Composer Eyes (Slice 1) — server↔brain perception RPC (requestId-correlated
  // via awaitSandboxEvent); never forwarded to the frontend.
  [EVENTS.OS_PERCEIVE_REQUEST]: { forward: false, schema: OsPerceiveRequestPayloadSchema },
  [EVENTS.OS_PERCEIVE_RESULT]: { forward: false, schema: OsPerceiveResultPayloadSchema },
  // Tier-0 verbs (Composer lane) — server↔brain verb RPC (requestId-correlated
  // via awaitSandboxEvent); never forwarded to the frontend.
  [EVENTS.OS_VERB_REQUEST]: { forward: false, schema: OsVerbRequestPayloadSchema },
  [EVENTS.OS_VERB_RESULT]: { forward: false, schema: OsVerbResultPayloadSchema },
  // The os relay RPCs (dictate, interact, capture, the composer's step lane and app recipes, the
  // inventory push): server↔runner only, requestId-correlated via awaitSandboxEvent (the lane's
  // progress and cancel by laneId, the inventory push by nothing), never forwarded to the
  // frontend. Their schemas (schemas/os-protocol.schema.ts) describe the frames; every consumer
  // still field-picks what it reads.
  [EVENTS.OS_DICTATE_REQUEST]: { forward: false, schema: OsDictateRequestSchema, from: 'server' },
  [EVENTS.OS_DICTATE_RESULT]: { forward: false, schema: OsDictateResultSchema, from: 'sandbox' },
  [EVENTS.OS_INTERACT_REQUEST]: { forward: false, schema: OsInteractRequestSchema, from: 'server' },
  [EVENTS.OS_INTERACT_RESULT]: { forward: false, schema: OsInteractResultSchema, from: 'sandbox' },
  [EVENTS.OS_CAPTURE_REQUEST]: { forward: false, schema: OsCaptureRequestSchema, from: 'server' },
  [EVENTS.OS_CAPTURE_RESULT]: { forward: false, schema: OsCaptureResultSchema, from: 'sandbox' },
  [EVENTS.OS_LANE_REQUEST]: { forward: false, schema: OsLaneRequestSchema, from: 'server' },
  [EVENTS.OS_LANE_PROGRESS]: { forward: false, schema: OsLaneProgressSchema, from: 'sandbox' },
  [EVENTS.OS_LANE_CANCEL]: { forward: false, schema: OsLaneCancelSchema, from: 'server' },
  [EVENTS.OS_LANE_RESULT]: { forward: false, schema: OsLaneResultSchema, from: 'sandbox' },
  [EVENTS.OS_RECIPE_REQUEST]: { forward: false, schema: OsRecipeRequestSchema, from: 'server' },
  [EVENTS.OS_RECIPE_CANCEL]: { forward: false, schema: OsRecipeCancelSchema, from: 'server' },
  [EVENTS.OS_RECIPE_RESULT]: { forward: false, schema: OsRecipeResultSchema, from: 'sandbox' },
  [EVENTS.OS_APP_INVENTORY]: { forward: false, schema: OsAppInventoryFrameSchema, from: 'sandbox' },
  [EVENTS.TERMINAL_EXEC]: { forward: false, schema: TerminalExecPayloadSchema, from: 'web' },
  [EVENTS.TERMINAL_RESULT]: { forward: false, schema: TerminalResultPayloadSchema, from: 'server' },
  // Vendor-engine sign-in. forward: false on BOTH legs is the contract, not an
  // omission: the box reports its state upward for the server to persist, and
  // Settings reads that stored value over HTTP precisely so showing "Connected"
  // never has to wake a sleeping box. Forwarding the status would make the panel
  // depend on a live runner; forwarding the logout command would let a runner
  // forge one at the frontend.
  [EVENTS.ENGINE_AUTH_STATUS]: { forward: false, schema: EngineAuthStatusPayloadSchema },
  [EVENTS.ENGINE_AUTH_LOGOUT]: { forward: false, schema: EngineAuthLogoutPayloadSchema },
  // Agent wallet (spec 2026-10-15 §2): the payment freeze, server → runner only, and a shop's UPI link, server → web
  // only (emitToUser). Neither ever rides the sandbox → frontend relay.
  [EVENTS.PURCHASE_FREEZE]: { forward: false, schema: PurchaseFreezePayloadSchema, from: 'server' },
  [EVENTS.PURCHASE_UPI_LINK]: { forward: false, schema: PurchaseUpiLinkPayloadSchema, from: 'server' },
  // Plan standing: box → server only, for the same reason as the sign-in state above.
  [EVENTS.ENGINE_STANDING]: { forward: false, schema: EngineStandingPayloadSchema },
  // The refresh ask (T2b): server → box only, to a box already awake.
  [EVENTS.ENGINE_STANDING_REFRESH]: { forward: false, schema: EngineStandingRefreshPayloadSchema },
  // Settings → Connect (engines T2a). The ask and the cancel go server → box only. Progress is
  // forward: false too, though the person does see it: the generic forward stamps a taskId and
  // a connect belongs to no task, so the server re-emits it to the person itself (emitToUser).
  [EVENTS.ENGINE_CONNECT]: { forward: false, schema: EngineConnectPayloadSchema },
  [EVENTS.ENGINE_CONNECT_CANCEL]: { forward: false, schema: EngineConnectCancelPayloadSchema },
  [EVENTS.ENGINE_CONNECT_PROGRESS]: { forward: false, schema: EngineConnectProgressPayloadSchema },
  // Several accounts (spec 2026-10-01): the live slot list, server → box only, at AGENT_READY.
  [EVENTS.ENGINE_SLOTS_SYNC]: { forward: false, schema: EngineSlotsSyncPayloadSchema },
} satisfies Record<EventName, EventContract>;

export type EventRegistry = typeof EVENT_REGISTRY;

// ============================================================================
// Derived: EventSchemas ({ name → Zod schema })
// ============================================================================

/** Wire names whose contract carries a Zod schema. */
export type EventSchemaKey = {
  [K in keyof EventRegistry]: EventRegistry[K] extends { schema: z.ZodTypeAny } ? K : never;
}[keyof EventRegistry];

/** { name → schema } with each key's EXACT schema type preserved (per-key parse typing). */
export type EventSchemasMap = {
  [K in EventSchemaKey]: EventRegistry[K] extends { schema: infer S } ? S : never;
};

/**
 * Map of event names to their validation schemas, derived from the schema-bearing
 * registry entries. Use for dynamic validation: `EventSchemas[eventName].parse(data)`.
 */
export const EventSchemas: EventSchemasMap = Object.fromEntries(
  (Object.entries(EVENT_REGISTRY) as Array<[EventName, EventContract]>)
    .filter(([, contract]) => contract.schema !== undefined)
    .map(([name, contract]) => [name, contract.schema]),
) as EventSchemasMap;

// ============================================================================
// Derived: forwarding allowlist
// ============================================================================

/**
 * O(1) membership set of the events the relay forwards to the frontend. The
 * registry is the runtime author of the forward decision; the legacy
 * FORWARD_EVENTS array (constants/events.ts) is a witnessed byte-identical mirror.
 */
export const FORWARD_EVENTS_SET: ReadonlySet<EventName> = new Set(
  (Object.entries(EVENT_REGISTRY) as Array<[EventName, EventContract]>)
    .filter(([, contract]) => contract.forward)
    .map(([name]) => name),
);

/** The forwarded event names, in EVENTS declaration order. */
export const FORWARD_EVENTS_DERIVED: readonly EventName[] = (
  Object.values(EVENTS) as EventName[]
).filter((name) => FORWARD_EVENTS_SET.has(name));

// ============================================================================
// Shrink-only allowlist: events that are legal WITHOUT a payload schema (v1)
// ============================================================================

/**
 * Events that carry a name but no Zod schema yet. This list may only SHRINK:
 * a new event MUST author a schema (its absence fails the allowlist lint in
 * tests/schemas/completeness.test.ts) unless it is deliberately added here.
 * As payloads get schemas, their names are removed from this list.
 */
export const SCHEMALESS_EVENTS: readonly EventName[] = [
  // Socket.IO's own transport events, not app frames: their arguments are the library's (a
  // disconnect reason, a connect error), never a payload anyone writes.
  'connect',
  'disconnect',
  'connect_error',
];
